import { apiClient } from "@/services/apiClient";

export interface MailMessageSummary {
  messageId: string;
  subject: string;
  senderName: string;
  senderEmail: string;
  receivedDateTime?: string;
  hasAttachments: boolean;
  recipientNames: string[];
}

export const getMailMessages = async (search?: string, top = 25): Promise<MailMessageSummary[]> => {
  const { data } = await apiClient.get<MailMessageSummary[]>("/api/email-filing/messages", {
    params: { search: search || undefined, top },
  });
  return data;
};

export interface SharePointSite {
  siteId: string;
  displayName: string;
  webUrl: string;
}

export interface SharePointDrive {
  driveId: string;
  driveName: string;
  webUrl: string;
}

export interface SharePointFolderItem {
  itemId: string;
  name: string;
  webUrl: string;
  hasChildFolders: boolean;
}

export interface SharePointDestination {
  destinationGuid?: string;
  siteId: string;
  siteName: string;
  siteUrl: string;
  driveId: string;
  driveName: string;
  folderItemId?: string;
  folderName: string;
  folderPath: string;
  isPinned: boolean;
  lastUsedDateTime?: string;
}

export type PinDestinationRequest = Omit<SharePointDestination, "destinationGuid" | "isPinned" | "lastUsedDateTime">;

export interface FileMessagesRequest {
  messageIds: string[];
  destination: PinDestinationRequest;
  fileNameOverrides?: Record<string, string>;
  retentionLabel?: string;
}

export interface RetentionLabelStatus {
  name?: string;
  isLabelAppliedExplicitly: boolean;
  labelAppliedBy?: string;
  labelAppliedDateTime?: string;
}

export interface EmailFilingUploadResult {
  messageId: string;
  fileName: string;
  succeeded: boolean;
  sharePointWebUrl?: string;
  error?: string;
}

export const fileMessages = async (request: FileMessagesRequest): Promise<EmailFilingUploadResult[]> => {
  const { data } = await apiClient.post<EmailFilingUploadResult[]>('/api/email-filing/file', request);
  return data;
};

export const searchSharePointSites = async (query: string): Promise<SharePointSite[]> => {
  const { data } = await apiClient.get<SharePointSite[]>("/api/email-filing/sites/search", { params: { q: query } });
  return data;
};

export const getSiteDrives = async (siteId: string): Promise<SharePointDrive[]> => {
  const { data } = await apiClient.get<SharePointDrive[]>(`/api/email-filing/sites/${siteId}/drives`);
  return data;
};

export const getFolderChildren = async (driveId: string, folderItemId?: string): Promise<SharePointFolderItem[]> => {
  const { data } = await apiClient.get<SharePointFolderItem[]>(`/api/email-filing/drives/${driveId}/children`, {
    params: { folderItemId: folderItemId || undefined },
  });
  return data;
};

export const getPinnedDestinations = async (): Promise<SharePointDestination[]> => {
  const { data } = await apiClient.get<SharePointDestination[]>("/api/email-filing/destinations/pinned");
  return data;
};

export const getRecentDestinations = async (): Promise<SharePointDestination[]> => {
  const { data } = await apiClient.get<SharePointDestination[]>("/api/email-filing/destinations/recent");
  return data;
};

export const pinDestination = async (request: PinDestinationRequest): Promise<SharePointDestination> => {
  const { data } = await apiClient.post<SharePointDestination>("/api/email-filing/destinations/pin", request);
  return data;
};

export const unpinDestination = async (destinationGuid: string): Promise<void> => {
  await apiClient.delete(`/api/email-filing/destinations/pin/${destinationGuid}`);
};
